import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { directSocketSave } from '@/views/editor/composable/useNote.ts'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { nextTick } from 'vue'

let savedFocusCell: HTMLElement | null = null
let savedFocusOffset: number = 0
let mouseEventCleanup: (() => void) | null = null
let originalHeightArray: number[] = []
let originalWidthArray: number[] = []
let heightDragStarted = false
let heightDragStartY = 0

export function useSize() {
	async function tableWidthMouseDown(event: MouseEvent) {
		event.preventDefault()
		const { cellTarget, positionWidth } = storeToRefs(useEditorStore())

		positionWidth.value = event.clientX
		const target = event.target as HTMLElement
		const cellId = target.dataset.cellId
		if (cellId) {
			cellTarget.value = document.getElementById(cellId) as HTMLTableCellElement
		} else {
			cellTarget.value = target.closest('td')
		}

		const selectTable = cellTarget.value?.closest('table')

		const activeElement = document.activeElement
		const activeTable = activeElement?.closest('table')
		if (activeElement && activeElement.getAttribute('contenteditable') === 'true' && activeTable === selectTable) {
			savedFocusCell = activeElement as HTMLElement
			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				savedFocusOffset = selection.getRangeAt(0).startOffset
			}
		} else {
			savedFocusCell = null
			savedFocusOffset = 0
		}

		useDeleteTable().selector()
		
		if (savedFocusCell && savedFocusCell.isConnected) {
			savedFocusCell.focus()
			try {
				const selection = window.getSelection()
				const range = document.createRange()
				const textNode = savedFocusCell.firstChild || savedFocusCell
				const offset = Math.min(savedFocusOffset, textNode.textContent?.length || 0)
				range.setStart(textNode, offset)
				range.setEnd(textNode, offset)
				selection?.removeAllRanges()
				selection?.addRange(range)
			} catch (e) {
			}
		}

		if (savedFocusCell) {
			savedFocusCell.style.caretColor = 'transparent'
			const cellToRestoreW = savedFocusCell
			const tableElW = cellToRestoreW.closest('table')
			const restoreCaretW = (e: Event) => {
				const target = e.target as HTMLElement
				if (target.closest('td') && !target.classList.contains('table--selector__width') && !target.classList.contains('table--selector__height')) {
					cellToRestoreW.style.removeProperty('caret-color')
					tableElW?.removeEventListener('mousedown', restoreCaretW)
					const td = target.closest('td')
					const cellDiv = td?.querySelector('div[contenteditable]') as HTMLElement
					if (cellDiv) {
						cellDiv.dispatchEvent(new FocusEvent('focusin', { bubbles: true }))
					}
				}
			}
			tableElW?.addEventListener('mousedown', restoreCaretW)
		}
		window.getSelection()?.removeAllRanges()

		if (selectTable) {
			useCreateTable().tableShapes(selectTable as HTMLTableElement)
		}

		const selectorElement = document.getElementById('editor--table__selector')
		const selectorDotElement = document.getElementById('editor--table__selector-dot')
		if (selectorElement && selectorElement.parentElement) {
			selectorElement.parentElement.removeChild(selectorElement)
		}
		if (selectorDotElement && selectorDotElement.parentElement) {
			selectorDotElement.parentElement.removeChild(selectorDotElement)
		}

		if (mouseEventCleanup) {
			mouseEventCleanup()
		}
		
		// mouseMove 등록을 한 번 더 지연 (history 저장 완전 보장)
		await nextTick()
		
		document.addEventListener('mousemove', tableWidthMouseMove)
		document.addEventListener('mouseup', tableWidthMouseUp)
		
		mouseEventCleanup = () => {
			document.removeEventListener('mousemove', tableWidthMouseMove)
			document.removeEventListener('mouseup', tableWidthMouseUp)
		}
	}

	function tableWidthMouseMove(event: MouseEvent) {
		const { cellTarget, positionWidth, tableSelector } = storeToRefs(useEditorStore())

		let targetCellIndex = 0
		for (const [rowIndex, row] of tableSelector.value.entries()) {
			for (const [cellIndex, cell] of row.entries()) {
				if (cellTarget.value.id === cell?.html?.id) {
					targetCellIndex = cellIndex
				}
			}
		}

		const tableX = event.clientX
		const selectTable = cellTarget.value.closest('table')

		// 안전망: mouseDown 누락 시를 대비한 초기화
		if (positionWidth.value === 0) {
			positionWidth.value = tableX
		}

		const mx = event.clientX - positionWidth.value
		positionWidth.value = event.clientX
		document.body.style.cursor = 'col-resize'

		selectTable.style.userSelect = 'none'
		selectTable.style.pointerEvents = 'none'

		let targetCell = null
		for (const [rowIndex, row] of tableSelector.value.entries()) {
			for (const [cellIndex, cell] of row.entries()) {
				if (cell && cell.html) {
					if (cellIndex === targetCellIndex && !cell.html.classList.contains('number--line')) {
						if (!targetCell) {
							targetCell = cell
						}

						if (cell.html.colSpan> 1) {
							const domCellIndex = cell.html.cellIndex - 1
							if (cellIndex < domCellIndex + cell.html.colSpan - 1) continue
						}

						const widthSelector = cell.html.querySelector('.table--selector__width')
						if (widthSelector) {
							widthSelector.classList.add('active')
						}
					}
				}
			}
		}

		if (!targetCell) return

		let width
		if (targetCell.html.style.width) {
			width = parseFloat(targetCell.html.style.width)
		} else {
			width = targetCell.html.getBoundingClientRect().width
		}
		width += mx

		if (width < 40) {
			width = 40
		}

		targetCell.html.style.width = `${width}px`

		const headerRow = selectTable.querySelector('tr:first-child')
		if (headerRow) {
			const numberLineCells = headerRow.querySelectorAll('td.number--line')
			if (numberLineCells[targetCellIndex + 1]) {
				;(numberLineCells[targetCellIndex + 1] as HTMLElement).style.width = `${width}px`
			}
		}
	}

	async function tableWidthMouseUp() {
		const { cellTarget, positionWidth } = storeToRefs(useEditorStore())
		
		try {
			if (!cellTarget.value) {
				return
			}
			
			const selectTable = cellTarget.value.closest('table')

			document.body.style.removeProperty('cursor')

			if (selectTable) {
				selectTable.style.removeProperty('user-select')
				selectTable.style.removeProperty('pointer-events')
			}

			// 드래그 완료 후 상태 초기화 (다음 드래그 준비)
			positionWidth.value = 0

			if (savedFocusCell && savedFocusCell.isConnected) {
				savedFocusCell.style.removeProperty('caret-color')
				savedFocusCell.blur()
			}
			savedFocusCell = null
			savedFocusOffset = 0
			
			directSocketSave()
			
			await nextTick()
			new UseHistory().push(false, { tableOperation: true })
			useDeleteTable().selector()
			setTimeout(() => useDeleteTable().selector(), 150)
		} finally {
			if (mouseEventCleanup) {
				mouseEventCleanup()
				mouseEventCleanup = null
			}
		}
	}

	async function tableHeightMouseDown(event: MouseEvent) {
		event.preventDefault()

		const { cellTarget, positionHeight } = storeToRefs(useEditorStore())
		positionHeight.value = event.clientY
		heightDragStarted = false
		heightDragStartY = event.clientY
		const target = event.target as HTMLElement
		const cellId = target.dataset.cellId
		if (cellId) {
			cellTarget.value = document.getElementById(cellId) as HTMLTableCellElement
		} else {
			cellTarget.value = target.closest('td')
		}

		const selectTable = cellTarget.value?.closest('table')

		const activeElement = document.activeElement
		const activeTable = activeElement?.closest('table')
		if (activeElement && activeElement.getAttribute('contenteditable') === 'true' && activeTable === selectTable) {
			savedFocusCell = activeElement as HTMLElement
			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				savedFocusOffset = selection.getRangeAt(0).startOffset
			}
		} else {
			savedFocusCell = null
			savedFocusOffset = 0
		}

		useDeleteTable().selector()
		
		if (savedFocusCell && savedFocusCell.isConnected) {
			savedFocusCell.focus()
			try {
				const selection = window.getSelection()
				const range = document.createRange()
				const textNode = savedFocusCell.firstChild || savedFocusCell
				const offset = Math.min(savedFocusOffset, textNode.textContent?.length || 0)
				range.setStart(textNode, offset)
				range.setEnd(textNode, offset)
				selection?.removeAllRanges()
				selection?.addRange(range)
			} catch (e) {
			}
		}

		if (savedFocusCell) {
			savedFocusCell.style.caretColor = 'transparent'
			const cellToRestoreH = savedFocusCell
			const tableElH = cellToRestoreH.closest('table')
			const restoreCaretH = (e: Event) => {
				const target = e.target as HTMLElement
				if (target.closest('td') && !target.classList.contains('table--selector__width') && !target.classList.contains('table--selector__height')) {
					cellToRestoreH.style.removeProperty('caret-color')
					tableElH?.removeEventListener('mousedown', restoreCaretH)
					const td = target.closest('td')
					const cellDiv = td?.querySelector('div[contenteditable]') as HTMLElement
					if (cellDiv) {
						cellDiv.dispatchEvent(new FocusEvent('focusin', { bubbles: true }))
					}
				}
			}
			tableElH?.addEventListener('mousedown', restoreCaretH)
		}
		window.getSelection()?.removeAllRanges()

		if (selectTable) {
			useCreateTable().tableShapes(selectTable as HTMLTableElement)
		}
		if (selectTable) {
			const allRows = selectTable.querySelectorAll('tr')
			originalHeightArray = []
			originalWidthArray = []
			
			const headerRow = allRows[0]
			if (headerRow) {
				const headerCells = headerRow.querySelectorAll('td.number--line')
				headerCells.forEach((cell: Element, index: number) => {
					if (index > 0) {
						const cellEl = cell as HTMLElement
						const width = cellEl.style.width ? parseFloat(cellEl.style.width) : 120
						originalWidthArray.push(Math.round(width))
					}
				})
			}
			
			for (let i = 1; i < allRows.length; i++) {
				const row = allRows[i]
				const numberLine = row.cells[0] as HTMLElement
				const firstDataCell = row.querySelector('td:not(.number--line)') as HTMLTableCellElement
				if (firstDataCell) {
					if (firstDataCell.dataset.type === 'image') {
						originalHeightArray.push(Math.round(firstDataCell.getBoundingClientRect().height))
					} else if (firstDataCell.rowSpan > 1) {
						if (numberLine) {
							originalHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
						} else {
							originalHeightArray.push(40)
						}
					} else {
						const innerDiv = firstDataCell.querySelector('div') as HTMLElement
						if (innerDiv && innerDiv.style.height) {
							originalHeightArray.push(Math.round(parseFloat(innerDiv.style.height)))
						} else if (numberLine) {
							originalHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
						} else {
							originalHeightArray.push(40)
						}
					}
				} else if (numberLine) {
					originalHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
				} else {
					originalHeightArray.push(40)
				}
			}
		}

		const selectorElement = document.getElementById('editor--table__selector')
		const selectorDotElement = document.getElementById('editor--table__selector-dot')
		if (selectorElement && selectorElement.parentElement) {
			selectorElement.parentElement.removeChild(selectorElement)
		}
		if (selectorDotElement && selectorDotElement.parentElement) {
			selectorDotElement.parentElement.removeChild(selectorDotElement)
		}

		if (mouseEventCleanup) {
			mouseEventCleanup()
		}
		
		document.addEventListener('mousemove', tableHeightMouseMove)
		document.addEventListener('mouseup', tableHeightMouseUp)
		
		mouseEventCleanup = () => {
			document.removeEventListener('mousemove', tableHeightMouseMove)
			document.removeEventListener('mouseup', tableHeightMouseUp)
		}
	}

	function tableHeightMouseMove(event: MouseEvent) {
		const { cellTarget, positionHeight, tableSelector } = storeToRefs(useEditorStore())

		let targetRowIndex = 0
		for (const [rowIndex, row] of tableSelector.value.entries()) {
			for (const [cellIndex, cell] of row.entries()) {
				if (cellTarget.value.id === cell?.html?.id) {
					targetRowIndex = rowIndex
				}
			}
		}

		const tableY = event.clientY
		const selectTable = cellTarget.value.closest('table')

		// 안전망: mouseDown 누락 시를 대비한 초기화
		if (positionHeight.value === 0) {
			positionHeight.value = tableY
		}

		// 드래그 시작 전: mousedown 지점에서 3px 이상 이동해야 드래그로 인식
		if (!heightDragStarted) {
			if (Math.abs(event.clientY - heightDragStartY) < 3) return
			heightDragStarted = true
			positionHeight.value = event.clientY
		}

		const my = event.clientY - positionHeight.value
		positionHeight.value = event.clientY

		if (Math.abs(my) < 1) return

		document.body.style.cursor = 'row-resize'

		selectTable.style.userSelect = 'none'
		selectTable.style.pointerEvents = 'none'

		let targetCells = []
		let hasDataCell = false

		for (const [rowIndex, row] of tableSelector.value.entries()) {
			for (const [cellIndex, cell] of row.entries()) {
				if (cell && cell.html) {
					if (rowIndex === targetRowIndex) {
						if (!cell.html.classList.contains('number--line')) {
							hasDataCell = true
							if (cell.html.rowSpan > 1) {
								const domRowIndex = cell.html.closest('tr').rowIndex - 1
								if (rowIndex < domRowIndex + cell.html.rowSpan - 1) continue
							}

							const heightSelector = cell.html.querySelector('.table--selector__height')
							if (heightSelector) {
								heightSelector.classList.add('active')
							}
						}
						targetCells.push(cell)
					}
				}
			}
		}

		if (!hasDataCell) return

		const processedCells = new Set<string>()
		let appliedHeight = 0
		for (const cell of targetCells) {
			if (!cell.html || cell.html.classList.contains('number--line')) continue
			if (processedCells.has(cell.html.id)) continue
			processedCells.add(cell.html.id)

			const isImageCell = cell.html.dataset.type === 'image'
			let cellHeight
			const cellInnerDiv = cell.html.querySelector('div')

			if (isImageCell) {
				cellHeight = cell.html.getBoundingClientRect().height
			} else if (cellInnerDiv && cellInnerDiv.style.height) {
				cellHeight = parseFloat(cellInnerDiv.style.height)
			} else if (cell.html.style.height) {
				cellHeight = parseFloat(cell.html.style.height)
			} else if (cellInnerDiv) {
				cellHeight = cellInnerDiv.getBoundingClientRect().height
			} else {
				cellHeight = cell.html.getBoundingClientRect().height
			}

			cellHeight += my

			const minHeight = cell.html.rowSpan > 1 ? 40 * cell.html.rowSpan : 40
			if (cellHeight < minHeight) {
				cellHeight = minHeight
			}

			cellHeight = Math.round(cellHeight)
			appliedHeight = cellHeight

			if (isImageCell) {
				cell.html.style.height = `${cellHeight}px`
			} else {
				if (cell.html.style.height) {
					cell.html.style.removeProperty('height')
				}
				if (cellInnerDiv) {
					cellInnerDiv.style.height = `${cellHeight}px`
				}
			}
		}

		if (appliedHeight > 0) {
			const allRows = selectTable.querySelectorAll('tr')
			const targetTr = allRows[targetRowIndex + 1]
			if (targetTr) {
				const numberLine = targetTr.cells[0] as HTMLElement
				if (numberLine?.classList.contains('number--line')) {
					numberLine.style.height = `${appliedHeight}px`
				}
			}
		}
	}

	async function tableHeightMouseUp() {
		const { cellTarget, positionHeight } = storeToRefs(useEditorStore())
		
		try {
			if (!cellTarget.value) {
				return
			}
			
			const selectTable = cellTarget.value.closest('table')

			document.body.style.removeProperty('cursor')

			if (selectTable) {
				selectTable.style.removeProperty('user-select')
				selectTable.style.removeProperty('pointer-events')

				selectTable.querySelectorAll('.table--selector__height.active').forEach((el: Element) => {
					el.classList.remove('active')
				})
			}

			positionHeight.value = 0

			if (savedFocusCell && savedFocusCell.isConnected) {
				savedFocusCell.style.removeProperty('caret-color')
				savedFocusCell.blur()
			}
			savedFocusCell = null
			savedFocusOffset = 0

			const allRows = selectTable?.querySelectorAll('tr')
			const newHeightArray: number[] = []
			const newWidthArray: number[] = []
			
			if (selectTable && allRows) {
				const headerRow = allRows[0]
				if (headerRow) {
					const headerCells = headerRow.querySelectorAll('td.number--line')
					headerCells.forEach((cell: Element, index: number) => {
						if (index > 0) {
							const cellEl = cell as HTMLElement
							const width = cellEl.style.width ? parseFloat(cellEl.style.width) : 120
							newWidthArray.push(Math.round(width))
						}
					})
				}
				
				for (let i = 1; i < allRows.length; i++) {
					const row = allRows[i]
					const numberLine = row.cells[0] as HTMLElement
					const firstDataCell = row.querySelector('td:not(.number--line)') as HTMLTableCellElement
					if (firstDataCell) {
						if (firstDataCell.dataset.type === 'image') {
							newHeightArray.push(Math.round(firstDataCell.getBoundingClientRect().height))
						} else if (firstDataCell.rowSpan > 1) {
							if (numberLine) {
								newHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
							} else {
								newHeightArray.push(40)
							}
						} else {
							const innerDiv = firstDataCell.querySelector('div') as HTMLElement
							if (innerDiv && innerDiv.style.height) {
								newHeightArray.push(Math.round(parseFloat(innerDiv.style.height)))
							} else if (numberLine) {
								newHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
							} else {
								newHeightArray.push(40)
							}
						}
					} else if (numberLine) {
						newHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
					} else {
						newHeightArray.push(40)
					}
				}
				
				// 새 높이를 data-size에 저장
				const firstDataRow = allRows[1]
				if (firstDataRow) {
					const firstDataCell = firstDataRow.querySelector('td:not(.number--line)') as HTMLElement
					if (firstDataCell) {
						const newSize = JSON.stringify({ width: newWidthArray, height: newHeightArray })
						firstDataCell.setAttribute('data-size', newSize)
					}
				}
			}
			
			await nextTick()
			await new UseHistory().push(false, { tableOperation: true })
			useDeleteTable().selector()
			setTimeout(() => useDeleteTable().selector(), 150)

			await nextTick()
			directSocketSave()
		} finally {
			if (mouseEventCleanup) {
				mouseEventCleanup()
				mouseEventCleanup = null
			}
		}
	}

	return {
		tableWidthMouseDown,
		tableHeightMouseDown,
	}
}
